import { FocusEventHandler, forwardRef, useContext, useEffect, useRef, useState } from 'react';

import { app, media } from 'context';
import Animate from '../Animate';
import Clickable from '../Clickable';
import Icon from '../Icon';
import Layout from '../Layout';
import Modal from '../Modal';
import { useListFocus } from 'hooks';
import { getLocations } from 'lib/location';
import { GRAY_900_HEX, ServiceableAddress, formatAddress, geocodeToAddress } from 'shared';

import AddressAutocompleteResults, { AddressAutocompleteResult } from './AddressAutocompleteResults';

/** Default offset for services-request flow (progress footer). */
const ADDRESS_AUTOCOMPLETE_DEFAULT_MOBILE_BOTTOM = 74;
export { ADDRESS_AUTOCOMPLETE_DEFAULT_MOBILE_BOTTOM };

interface Props {
  address?: ServiceableAddress | null;
  placeholder: string;
  handleClose?: VoidFunction;
  onSelect: (address: ServiceableAddress) => void;
  /**
   * When there is a committed `address` and the user edits the input so `value` no longer matches
   * `formatAddress(address)` (e.g. backspace), the parent should clear committed address (e.g. set null).
   */
  onCommittedAddressInvalidated?: () => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  paddingBottom?: number;
  /** Shown below the divider on the mobile fixed search strip (e.g. services-request CTA). */
  mobileFixedFooter?: React.ReactNode;
  /**
   * Distance from viewport bottom to the mobile fixed search bar (so it clears app footers).
   * Services request uses ~74; vendor wizard should pass a larger value to clear its footer.
   */
  mobileSearchBarBottomOffset?: number;
  mobileInlineLayout?: boolean;
  appearanceVariant?: 'default' | 'wizardDesktop' | 'wizardMobile';
}

const AddressAutocompleteInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      onFocus,
      onSelect,
      onCommittedAddressInvalidated,
      placeholder,
      onBlur,
      paddingBottom,
      address,
      mobileFixedFooter,
      mobileSearchBarBottomOffset = ADDRESS_AUTOCOMPLETE_DEFAULT_MOBILE_BOTTOM,
      mobileInlineLayout = false,
      appearanceVariant = 'default'
    },
    ref
  ) => {
    const { ipLocation } = useContext(app);
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = ref ?? internalRef;
    const origin = ipLocation || undefined;
    const [searchValue, setSearchValue] = useState<string>(address ? formatAddress(address) : '');
    const [results, setResults] = useState<AddressAutocompleteResult[]>([]);
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
    const formattedCommittedAddress = address ? formatAddress(address) : '';
    const visiblePlaceholder = searchValue === '' ? placeholder : '';
    const divId = 'address-autocomplete-input';
    const [bottomSheetIsVisible, setBottomSheetIsVisible] = useState(false);
    const bottomSheetInputRef = useRef<HTMLInputElement>(null);

    const onLocationSelect = async (result: AddressAutocompleteResult | null) => {
      if (!result) {
        return;
      }

      setSearchValue(result.display);
      setIsInputFocused(false);
      setResults([]);
      setFocusedIndex(-1);

      geocodeToAddress(result.display).then(addr => {
        if (addr.line1) {
          setBottomSheetIsVisible(false);
          onSelect({ ...addr, id: -1, line2: null } as ServiceableAddress);
        }
      });
    };

    const [focusedIndex, setFocusedIndex] = useListFocus({
      divId,
      autoSelectFirstItem: searchValue.length > 0,
      size: results.length,
      handleEnter: index => {
        onLocationSelect(results[index]);
      }
    });

    useEffect(() => {
      if (formattedCommittedAddress === '') return;
      if (isInputFocused) return;
      setSearchValue(formattedCommittedAddress);
    }, [formattedCommittedAddress, isInputFocused]);

    const handleChange = (value: string) => {
      setSearchValue(value);

      if (address && value !== formattedCommittedAddress) {
        onCommittedAddressInvalidated?.();
      }

      getLocations({
        input: value,
        origin,
        types: ['address'],
        country: ['us'],
        callback: locResults => {
          setResults(
            locResults
              .filter(r => r.types.some(t => ['premise', 'subpremise'].includes(t)))
              .slice(0, isBottomSheet ? 5 : 3)
              .map(r => ({
                display: r.description,
                placeId: r.place_id
              }))
          );
        }
      });
    };

    const { contentPaddingX } = useContext(app);
    const { xs, sm, isMobile } = useContext(media);
    const isBottomSheet = xs || sm;

    const inputBaseStyle: React.CSSProperties = {
      borderStyle: 'none',
      outline: 'none',
      background: 'none',
      color: GRAY_900_HEX,
      zIndex: 1,
      minWidth: 0,
      flex: 1,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    };

    const openMobileSearchModal = () => {
      setIsInputFocused(true);
      setBottomSheetIsVisible(true);
      setTimeout(() => {
        bottomSheetInputRef.current?.focus();
      }, 100);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 200);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 320);
    };

    const inlineMobileField = isBottomSheet && mobileInlineLayout;
    const isWizardDesktop = appearanceVariant === 'wizardDesktop' && !isBottomSheet;
    const isWizardMobile = appearanceVariant === 'wizardMobile' && mobileInlineLayout && isBottomSheet;

    const desktopFontSize = isWizardDesktop ? 20 : isBottomSheet ? 18 : 24;
    const desktopLineHeight = isWizardDesktop ? '30px' : isBottomSheet ? '28px' : '36px';
    const inlineMobileFontSize = isWizardMobile ? 16 : 18;
    const inlineMobileLineHeight = isWizardMobile ? '24px' : '28px';
    const inputLeftMargin = isWizardMobile ? 8 : isBottomSheet && !isWizardDesktop ? 8 : isWizardDesktop ? 16 : 16;

    let input = (
      <input
        data-cy="location-search-input"
        onFocus={e => {
          if (inlineMobileField) {
            return;
          }
          setIsInputFocused(true);
          setBottomSheetIsVisible(true);

          if (isBottomSheet) {
            openMobileSearchModal();
          }

          onFocus?.(e);
        }}
        autoComplete="off"
        placeholder={visiblePlaceholder}
        autoFocus={!isBottomSheet}
        readOnly={inlineMobileField}
        tabIndex={inlineMobileField ? -1 : undefined}
        ref={inputRef}
        spellCheck={false}
        id={divId}
        style={{
          ...inputBaseStyle,
          fontSize: inlineMobileField ? inlineMobileFontSize : desktopFontSize,
          lineHeight: inlineMobileField ? inlineMobileLineHeight : desktopLineHeight,
          marginLeft: inlineMobileField ? inputLeftMargin : isWizardDesktop ? 0 : isBottomSheet ? 8 : 16,
          pointerEvents: inlineMobileField ? 'none' : undefined
        }}
        value={searchValue}
        onChange={({ target: { value } }) => {
          if (!isInputFocused) {
            setIsInputFocused(true);
          }
          handleChange(value);
        }}
        onBlur={onBlur}
      />
    );

    const pinGap = isWizardMobile ? 8 : isWizardDesktop ? 16 : isBottomSheet ? 8 : 16;
    const pinSize = isWizardDesktop ? 'md' : isBottomSheet ? 'md' : 'xl';

    const inputContainer =
      mobileInlineLayout && isBottomSheet ? (
        <Clickable
          onClick={() => {
            openMobileSearchModal();
          }}
          style={{ width: '100%', minWidth: 0, display: 'block' }}
        >
          <Layout align="center" direction="row" gap={pinGap} width="100%" minWidth={0}>
            <Layout flexShrink={0}>
              <Icon size="md" name="addressPinOutline" color="gray-900" />
            </Layout>
            <Layout flexGrow={1} minWidth={0} align="center">
              {input}
            </Layout>
          </Layout>
        </Clickable>
      ) : (
        <Layout align="center" direction="row" gap={pinGap} width="100%" minWidth={0}>
          {isMobile && bottomSheetIsVisible ? (
            <Clickable
              onClick={() => setBottomSheetIsVisible(false)}
              style={{ display: 'inline-flex', padding: 16, flexShrink: 0 }}
            >
              <Icon stroke={2} name="leftChevron" color="blue-500" size={20} />
            </Clickable>
          ) : (
            <Layout flexShrink={0}>
              <Icon size={pinSize} name="addressPinOutline" color="gray-900" />
            </Layout>
          )}
          <Layout flexGrow={1} minWidth={0} align="center">
            {input}
          </Layout>
        </Layout>
      );

    const content = (
      <Layout paddingBottom={paddingBottom} width="100%" minWidth={0} position="relative" zIndex={1}>
        {inputContainer}
        {isMobile ? null : <Layout color="purple-gradient" height={2} marginTop={12} />}
        {isInputFocused && (
          <Layout
            position="absolute"
            top={54}
            zIndex={10}
            borderRadius={16}
            boxShadow="2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)"
            color="white"
            width="100%"
            minWidth={0}
          >
            <AddressAutocompleteResults
              results={results}
              onLocationSelect={onLocationSelect}
              searchValue={searchValue}
              focusedIndex={focusedIndex}
              showGoogleAttribution={false}
            />
          </Layout>
        )}
      </Layout>
    );

    return isBottomSheet ? (
      <>
        {mobileInlineLayout ? (
          <Layout width="100%" minWidth={0} paddingBottom={paddingBottom}>
            {inputContainer}
            <Layout color="purple-gradient" height={2} marginTop={12} />
          </Layout>
        ) : (
          <Layout
            position="fixed"
            bottom={mobileSearchBarBottomOffset}
            left={0}
            right={0}
            paddingX={24}
            paddingTop={24}
            paddingBottom={32}
            color="white"
            width="100%"
            minWidth={0}
            __style={{ boxSizing: 'border-box' }}
          >
            {inputContainer}
            <Layout color="purple-gradient" height={2} marginTop={12} />
            {mobileFixedFooter != null ? <Layout marginTop={32}>{mobileFixedFooter}</Layout> : null}
          </Layout>
        )}
        <Modal
          isVisible={bottomSheetIsVisible}
          paddingX={contentPaddingX}
          onClose={() => setBottomSheetIsVisible(false)}
          showCloseButton={false}
        >
          <Layout minWidth={0}>
            <Layout
              paddingRight={24}
              paddingTop={24}
              paddingBottom={12}
              position="relative"
              align="center"
              width="100%"
              minWidth={0}
            >
              <Clickable
                onClick={() => setBottomSheetIsVisible(false)}
                style={{ display: 'inline-flex', padding: 16, flexShrink: 0 }}
              >
                <Icon name="leftChevron" color="gray-900" size={20} />
              </Clickable>
              <Layout flexGrow={1} minWidth={0}>
                <input
                  data-cy="location-search-input-mobile"
                  onFocus={e => {
                    setIsInputFocused(true);
                    onFocus?.(e);
                  }}
                  autoComplete="off"
                  placeholder={visiblePlaceholder}
                  autoFocus={!isBottomSheet}
                  ref={bottomSheetInputRef}
                  spellCheck={false}
                  id={divId}
                  style={{
                    ...inputBaseStyle,
                    fontSize: 18,
                    lineHeight: '28px',
                    height: 30,
                    marginLeft: 8
                  }}
                  value={searchValue}
                  onChange={({ target: { value } }) => handleChange(value)}
                  onBlur={onBlur}
                />
              </Layout>
            </Layout>
            {isInputFocused && (
              <AddressAutocompleteResults
                results={results}
                onLocationSelect={onLocationSelect}
                searchValue={searchValue}
                focusedIndex={focusedIndex}
                showGoogleAttribution={false}
                isBottomSheet
              />
            )}
          </Layout>
        </Modal>
      </>
    ) : (
      <Animate>{content}</Animate>
    );
  }
);

AddressAutocompleteInput.displayName = 'AddressAutocompleteInput';

export default AddressAutocompleteInput;
